import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Text, Box, VStack, Button, Container } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { useFormik, FormikProvider } from "formik";
import { CreateConsultationRequest } from "~/../../sdk/consultations";
import { FullBleedSection } from "~/components";
import { routes } from "~/constants/routes";
import {
  useConsultationsCreateMutation,
  useDiagnosticUserProductShowQuery,
} from "~/store/api";
import {
  WomensFertilityIntakeForm,
  MensFertilityIntakeForm,
  type WomensFertilityFormValues,
  type MensFertilityFormValues,
} from "./IntakePage/index";

const INTAKE_FORMS = {
  womens_fertility_consultation: WomensFertilityIntakeForm,
  mens_fertility_consultation: MensFertilityIntakeForm,
} as const;

type ConsultationSlug = keyof typeof INTAKE_FORMS;

const IntakePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { diagnostic_user_product_id } = useParams<{
    diagnostic_user_product_id: string;
  }>();

  if (!diagnostic_user_product_id) {
    throw new Error("diagnostic_user_product_id is required");
  }

  const labTestId = searchParams.get("lab_test_id");

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createConsultation, { isLoading: isSubmitting }] =
    useConsultationsCreateMutation();

  const { data: diagnosticUserProduct, isLoading } =
    useDiagnosticUserProductShowQuery(diagnostic_user_product_id);

  const consultationSlug = diagnosticUserProduct?.diagnostic_product
    .consultation_slug as ConsultationSlug | null;

  const labTest = labTestId
    ? diagnosticUserProduct?.lab_tests.find((lt) => lt.id === Number(labTestId))
    : null;

  const labTestMediaIds = labTest?.result_media_id
    ? [labTest.result_media_id]
    : [];

  const FormComponent = consultationSlug
    ? INTAKE_FORMS[consultationSlug]
    : null;

  useEffect(() => {
    document.title = t("diagnosticDashboard.intake.pageTitle");
  }, [t]);

  const initialValues = FormComponent?.getInitialValues() ?? {};
  const validationSchema = FormComponent?.getValidationSchema?.(t);

  const onSubmit = async (
    values: WomensFertilityFormValues | MensFertilityFormValues,
  ): Promise<void> => {
    try {
      if (!FormComponent || !consultationSlug) {
        throw new Error("Form component or consultation slug not available");
      }

      const intakeAnswers = FormComponent.buildIntakeAnswers(
        values as any,
        labTestMediaIds,
      );

      const payload: CreateConsultationRequest = {
        diagnostic_user_product_id: diagnostic_user_product_id!,
        intake_answers: intakeAnswers,
        ...(labTest && { lab_test_id: labTest.id }),
      };

      await createConsultation(payload).unwrap();
      navigate(routes.diagnosticsIntakeComplete);
    } catch (error) {
      console.error("Error submitting intake form:", error);
      Sentry.captureException(error);

      let errorMessage: string;

      const errorData = error as any;
      if (errorData?.status === 422) {
        if (errorData?.data?.error) {
          errorMessage = errorData.data.error;
        } else {
          errorMessage = t("diagnosticDashboard.intake.errorMessage");
        }
      } else {
        errorMessage =
          errorData?.data?.error ||
          t("diagnosticDashboard.intake.errorMessage");
      }

      setErrorMessage(errorMessage);
    }
  };

  const formik = useFormik({
    initialValues: initialValues as any,
    onSubmit,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  if (isLoading) {
    return (
      <FullBleedSection bg="bg-main" flexGrow={1} h="100%" justify="center">
        <Text>Loading...</Text>
      </FullBleedSection>
    );
  }

  if (diagnosticUserProduct && !FormComponent) {
    const errorMsg = `Unmapped consultation slug: ${consultationSlug}`;
    console.error(errorMsg);
    Sentry.captureMessage(errorMsg, "error");

    return (
      <FullBleedSection bg="bg-main" flexGrow={1} h="100%" justify="center">
        <Container maxW="39rem" px={{ base: 4, md: 8 }}>
          <VStack spacing={6} align="center">
            <VStack spacing={3}>
              <Text
                fontSize={{ base: "xl", lg: "2xl" }}
                fontWeight="bold"
                textAlign="center"
                fontFamily="Moderat Extended"
              >
                {t("diagnosticDashboard.intake.errorHeading")}
              </Text>
              <Text
                fontSize={{ base: "md", lg: "lg" }}
                color="fg-main"
                textAlign="center"
              >
                {t("diagnosticDashboard.intake.errorDescription")}
              </Text>
            </VStack>
            <Button
              size="th-md"
              variant="th-primary"
              onClick={() => navigate(routes.diagnosticsDashboard)}
            >
              {t("diagnosticDashboard.intake.backToDashboard")}
            </Button>
          </VStack>
        </Container>
      </FullBleedSection>
    );
  }

  return (
    <FullBleedSection bg="bg-main" flexGrow={1} h="100%" justify="flex-start">
      <Container
        maxW="39rem"
        px={{ base: 4, md: 8 }}
        pt={{ base: 10, md: 16 }}
        pb={{ base: "15rem" }}
      >
        <FormikProvider value={formik}>
          <VStack
            as="form"
            onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
            w="full"
            alignItems="flex-start"
            spacing={6}
          >
            <VStack w="full" alignItems="flex-start" spacing={2}>
              <Text
                fontSize={{ base: "2xl", lg: "3xl" }}
                fontWeight="bold"
                lineHeight="1.2"
                fontFamily="Moderat Extended"
              >
                {t("diagnosticDashboard.intake.heading")}
              </Text>
              <Text fontSize={{ base: "md", lg: "lg" }} color="fg-main">
                {t("diagnosticDashboard.intake.subheading")}
              </Text>
            </VStack>

            {errorMessage && (
              <Box
                bg="red.100"
                color="red.700"
                p={3}
                borderRadius="md"
                width="100%"
              >
                {errorMessage}
              </Box>
            )}

            {FormComponent && (
              <FormComponent labTestMediaIds={labTestMediaIds} />
            )}

            <Button
              type="submit"
              size="th-md"
              variant="th-primary"
              width="100%"
              isLoading={formik.isSubmitting || isSubmitting}
              disabled={formik.isSubmitting || isSubmitting || !formik.isValid}
            >
              {t("diagnosticDashboard.intake.submitButton")}
            </Button>
          </VStack>
        </FormikProvider>
      </Container>
    </FullBleedSection>
  );
};

// eslint-disable-next-line import/no-default-export
export default IntakePage;
